import { ExtractionSpecification } from './extractionSpecification';
import { CreatePackageParameters, moveLibraryElement, ShippingWizard } from './wizard';
import { LibraryListItem } from '@/shared/helpers/api';

export type LibraryExtractionSpecification = {
    libraryName: string;
    includeLibraryGroups: boolean;
    includeAccessGroups: boolean;
    includeSubscriptions: boolean;
    includeAliases: boolean;
    includeCodeSets: boolean;
    includeAccessPolicies: boolean;
};

function newLibraryExtractionSpecification(libraryName: string): LibraryExtractionSpecification {
    return {
        libraryName,
        includeLibraryGroups: true,
        includeAccessGroups: true,
        includeSubscriptions: false,
        includeAliases: false,
        includeCodeSets: true,
        includeAccessPolicies: false
    };
}

export function newLibraryDefinitionWizard(elementList: ExtractionSpecification[]): ShippingWizard<LibraryExtractionSpecification> {
    const elements = elementList as LibraryExtractionSpecification[];

    async function addLibrary(library: LibraryListItem): Promise<void> {
        elements.push(newLibraryExtractionSpecification(library.name));
    }

    function removeLibrary(name: string): void {
        const index = elements.findIndex(x => x.libraryName === name);
        elements.splice(index, 1);
    }

    async function loadSaved(options: object, availableLibraries: LibraryListItem[]): Promise<string[]> {
        const result = libraryDefinitionSpecsFromSaved(options, availableLibraries);
        elements.push(...result.specifications);
        return result.dropped;
    }

    function finish(): CreatePackageParameters {
        const options = {
            libraries: elements
        };

        const description = options.libraries.length === 1
            ? `Library definition: ${options.libraries[0].libraryName}`
            : `Library definitions: ${options.libraries.map(x => x.libraryName).join(', ')}.`;

        return {
            packageType: 'LibraryDefinitionsPackage',
            description,
            options
        };
    }

    return {
        type: 'LibraryDefinitions',
        addLibrary,
        removeLibrary,
        moveLibrary: (name: string, offset: number) => moveLibraryElement(elements, name, offset),
        loadSaved,
        finish,
        elements
    };
}

export function libraryDefinitionSpecsFromSaved(
    options: object,
    availableLibraries: LibraryListItem[]): { specifications: LibraryExtractionSpecification[]; dropped: string[] } {
    const saved = (options as { libraries?: LibraryExtractionSpecification[] }).libraries ?? [];
    const availableNames = new Set(availableLibraries.map(x => x.name));
    const specifications: LibraryExtractionSpecification[] = [];
    const dropped: string[] = [];

    for (const spec of saved) {
        if (!availableNames.has(spec.libraryName)) {
            dropped.push(`Library ${spec.libraryName} no longer exists`);
            continue;
        }
        specifications.push({ ...newLibraryExtractionSpecification(spec.libraryName), ...spec });
    }

    return { specifications, dropped };
}
